import React, { useEffect, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { getUserStories, printStory } from '../../api/stories';
import { getUserAddress, updateUserAddress, hasCompleteAddress, Address, AddressUpdateRequest } from '../../api/address';
import { Story } from '../../types/story';
import NotificationModal from '../NotificationModal/NotificationModal';
import './ProfilePage.css';

// Status mapping for user-friendly display
const statusMapping: Record<string, string> = {
  pending: 'Pending',
  completed: 'Completed',
  processing: 'Processing',
  under_review: 'Under Review',
  printing: 'Printing',
  printed: 'Printed',
  error: 'Error'
};

const ProfilePage: React.FC = () => {
  const { user, logout } = useUser();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  
  // Address state
  const [address, setAddress] = useState<Address | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressUpdateRequest>({
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  // Printing state
  const [printingStoryId, setPrintingStoryId] = useState<string | null>(null);

  // Notification modal state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    actionText?: string;
    onAction?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch stories and address in parallel
        const [userStories, userAddress] = await Promise.all([
          getUserStories(),
          getUserAddress()
        ]);
        
        // Handle stories
        if (!Array.isArray(userStories)) {
          setDebugInfo(`Received non-array response: ${JSON.stringify(userStories)}`);
          setStories([]);
        } else {
          setStories(userStories);
          setDebugInfo(null);
        }
        
        // Handle address
        setAddress(userAddress);
        
        setError(null);
      } catch (err) {
        setError('Failed to load data. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
  };

  const handleEditAddress = () => {
    setIsEditingAddress(true);
    setEditingAddress({
      street_address: address?.street_address || '',
      city: address?.city || '',
      state: address?.state || '',
      postal_code: address?.postal_code || '',
      country: address?.country || ''
    });
  };

  const handleSaveAddress = async () => {
    try {
      setAddressLoading(true);
      setAddressError(null);
      
      const updatedAddress = await updateUserAddress(editingAddress);
      setAddress(updatedAddress);
      setIsEditingAddress(false);
    } catch (err: any) {
      setAddressError(err.message);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleCancelEditAddress = () => {
    setIsEditingAddress(false);
    setAddressError(null);
  };

  const handlePrintStory = async (storyId: string) => {
    try {
      setPrintingStoryId(storyId);
      
      // Check if user has complete address
      if (!address || !hasCompleteAddress(address)) {
        setNotification({
          isOpen: true,
          title: 'Address Required',
          message: 'Please set your complete shipping address before printing your story.',
          type: 'warning',
          actionText: 'Add Address',
          onAction: () => {
            setNotification(prev => ({ ...prev, isOpen: false }));
            if (!isEditingAddress) {
              handleEditAddress();
            }
          }
        });
        return;
      }
      
      const result = await printStory(storyId);
      
      // Update the story status in the local state
      setStories(prevStories => 
        prevStories.map(story => 
          story.id === storyId 
            ? { ...story, status: result.status as any }
            : story
        )
      );
      
      setNotification({
        isOpen: true,
        title: 'Print Request Submitted',
        message: result.message,
        type: 'success'
      });
    } catch (err: any) {
      setNotification({
        isOpen: true,
        title: 'Print Failed',
        message: err.message,
        type: 'error'
      });
    } finally {
      setPrintingStoryId(null);
    }
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  if (!user) {
    return <div className="profile-container">Loading profile...</div>;
  }

  const canShowPrintButton = (story: Story) => {
    return story.status === 'completed' && (story.pdf_file || story.metadata?.pdf_file);
  };

  const canShowPdf = (story: Story) => {
    return ['completed', 'printing', 'printed'].includes(story.status) && 
           (story.pdf_file || story.metadata?.pdf_file);
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar">
          {user.first_name.charAt(0).toUpperCase()}
        </div>
        <div className="profile-header-content">
          <h1 className="profile-name">{user.first_name} {user.last_name}</h1>
        </div>
      </div>
      
      <div className="profile-details">
        <div className="profile-section">
          <h2>My Stories</h2>
          {loading ? (
            <p>Loading your stories...</p>
          ) : error ? (
            <p className="error-message">{error}</p>
          ) : debugInfo ? (
            <div>
              <p>Debugging information:</p>
              <pre className="debug-info">{debugInfo}</pre>
            </div>
          ) : stories.length === 0 ? (
            <p>You haven't created any stories yet.</p>
          ) : (
            <div className="stories-grid">
              {stories.map((story) => (
                <div key={story.id} className="story-card">
                  <h3>{story.title || 'Untitled Story'}</h3>
                  <p>Status: <span className={`status-${story.status}`}>{statusMapping[story.status] || story.status}</span></p>
                  <p>Created: {new Date(story.created_at || story.createdAt).toLocaleDateString()} at {new Date(story.created_at || story.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                  
                  <div className="story-actions">
                    {canShowPdf(story) && (
                      <a 
                        href={story.pdf_file || story.metadata?.pdf_file} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="pdf-link"
                      >
                        View PDF
                      </a>
                    )}
                    
                    {canShowPrintButton(story) && (
                      <button
                        className="print-button"
                        onClick={() => handlePrintStory(story.id)}
                        disabled={printingStoryId === story.id}
                      >
                        {printingStoryId === story.id ? 'Printing...' : 'Print'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="profile-section">
          <h2>Shipping Address</h2>
          {addressError && <p className="error-message">{addressError}</p>}
          
          {!isEditingAddress ? (
            <div>
              {address && hasCompleteAddress(address) ? (
                <div className="address-display">
                  <div className="profile-info-item">
                    <span className="profile-label">Street:</span>
                    <span className="profile-value">{address.street_address}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-label">City:</span>
                    <span className="profile-value">{address.city}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-label">State:</span>
                    <span className="profile-value">{address.state}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-label">Postal Code:</span>
                    <span className="profile-value">{address.postal_code}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-label">Country:</span>
                    <span className="profile-value">{address.country}</span>
                  </div>
                  <button className="edit-address-button" onClick={handleEditAddress}>
                    Edit Address
                  </button>
                </div>
              ) : (
                <div>
                  <p>No shipping address set. You'll need to add one to print stories.</p>
                  <button className="edit-address-button" onClick={handleEditAddress}>
                    Add Address
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="address-form">
              <div className="form-group">
                <label htmlFor="street_address">Street Address:</label>
                <input
                  type="text"
                  id="street_address"
                  value={editingAddress.street_address}
                  onChange={(e) => setEditingAddress({...editingAddress, street_address: e.target.value})}
                  placeholder="123 Main Street"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="city">City:</label>
                <input
                  type="text"
                  id="city"
                  value={editingAddress.city}
                  onChange={(e) => setEditingAddress({...editingAddress, city: e.target.value})}
                  placeholder="New York"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="state">State:</label>
                  <input
                    type="text"
                    id="state"
                    value={editingAddress.state}
                    onChange={(e) => setEditingAddress({...editingAddress, state: e.target.value})}
                    placeholder="NY"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="postal_code">Postal Code:</label>
                  <input
                    type="text"
                    id="postal_code"
                    value={editingAddress.postal_code}
                    onChange={(e) => setEditingAddress({...editingAddress, postal_code: e.target.value})}
                    placeholder="10001"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="country">Country:</label>
                <input
                  type="text"
                  id="country"
                  value={editingAddress.country}
                  onChange={(e) => setEditingAddress({...editingAddress, country: e.target.value})}
                  placeholder="USA"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  className="save-address-button" 
                  onClick={handleSaveAddress}
                  disabled={addressLoading}
                >
                  {addressLoading ? 'Saving...' : 'Save Address'}
                </button>
                <button 
                  className="cancel-address-button" 
                  onClick={handleCancelEditAddress}
                  disabled={addressLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="profile-section">
          <h2>Account Information</h2>
          <div className="profile-info-item">
            <span className="profile-label">Email:</span>
            <span className="profile-value">{user.email}</span>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        actionText={notification.actionText}
        onAction={notification.onAction}
      />
    </div>
  );
};

export default ProfilePage; 